/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { PaymentElement } from '@stripe/react-stripe-js';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { PaymentCycle } from "../../models/enumerations/payment-cycle";
import { Loader } from "../loader/loader";
import OrganizationSubscriptionService from "../../utilities/services/organization-subscription-service";
import { WarningItem } from "../warning-item/warning-item";
import { Button } from "../button/button";
import { Tag as TagComponent } from "../tag/tag";
import OrganizationService from "../../utilities/services/organization-service";
import { PlanType } from "../../models/enumerations/plan-type";
import { UserLimitPlanType } from "../../models/enumerations/user-limit";
import { LicenseLimitPlanType } from "../../models/enumerations/license-limit";
import FunctionService from "../../utilities/services/function-service";
import moment from "moment";
import { PlanIdPlanType } from "../../models/enumerations/plan-id";
import { TextTypeInput } from "../forms";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PlanTypeLabels } from "../../models/plan-type";
import { Price } from "../../models/interfaces/price";
import { Product } from "../../models/interfaces/product";
import Stripe from "stripe";
import { OrganizationSubscription } from "../../models/interfaces/organization-subscription";
import { PaymentCycleLabel } from "../../models/payment-cycle";
import { OrganizationType, OrganizationTypeLabel } from "../../models/enumerations/organization-type";
import { deleteField } from "firebase/firestore";
import { RadioGroupItem } from "../radio-group/radio-group-item";

const COMPONENT_CLASS = "c-checkoutform";
const FORM_COMPONENT_CLASS = "p-link-create-account";

interface CheckoutFormUpdateProps {
  setUpgradeModalOpen: Function;
  onChangePlan?: () => void,
  returnUrl: string;
  paymentFailed: Function;
  paymentSuccessful: Function;
  paymentCycle: PaymentCycle;
  selectedProduct: Product;
  selectedPrice: Price;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  orgSub: OrganizationSubscription;
}

interface ValidationErrors {
  [key: string]: string;
}

const CheckoutFormUpdate: React.FC<CheckoutFormUpdateProps> = ({ selectedPrice, selectedProduct, setUpgradeModalOpen, onChangePlan, paymentSuccessful, paymentFailed, returnUrl, paymentCycle, isLoading, setIsLoading, orgSub }) => {

  const { t } = useTranslation();
  const { state } = useAuthState();
  const stripe = useStripe();
  const stripe2: Stripe = require('stripe')(process.env.REACT_APP_STRIPE_SECRET_KEY);
  const [clientSecret, setClientSecret] = useState('');
  const elements = useElements();
  const [customer, setCustomer] = useState<Stripe.Customer>();
  const [planType, setPlanType] = useState<PlanType>(selectedProduct.id ? PlanIdPlanType[selectedProduct.id] : PlanType.Core);
  const [paymentFailedBanner, setPaymentFailedBanner] = useState<boolean>(false);
  const [newPrice, setNewPrice] = useState<number>();
  const [totalPrice, setTotalPrice] = useState<number>();
  const [proratedSum, setProratedSum] = useState<number>();
  const [discountAmount, setDiscountAmount] = useState<number>();
  const [coupons, setCoupons] = useState([] as any[]);
  const [subscriptionCoupon, setSubscriptionCoupon] = useState<string>();
  const [allCoupons, setAllCoupons] = useState<any>();
  const [couponValue, setCouponValue] = useState<any>();
  const [discountError, setDiscountError] = useState<string>('');
  const [couponExists, setCouponExists] = useState<boolean>(true);

  const params = new URLSearchParams(window.location.search);
  const couponId = params.get("couponId");

  const methods = useForm<any>();
  const {
    register,
  } = methods;

  let priceToShow = "";
  if (selectedPrice && selectedPrice.unit_amount) {
    priceToShow = (selectedPrice.unit_amount / 100).toFixed(2);
  }

  if (elements) {
    const element = elements!.getElement('payment')
    if (element) {
      element.on('ready', () => {
        setIsLoading(false);
      })
    }
  }

  useEffect(() => {
    if (selectedProduct.id && selectedPrice.unit_amount !== undefined) {
      const purchasedPlanType = PlanIdPlanType[selectedProduct.id];

      setPlanType(purchasedPlanType);

      const price = Number((selectedPrice.unit_amount / 100).toFixed(2));
      setTotalPrice(price);
    }

  }, [selectedPrice.unit_amount, selectedProduct.id]);

  useEffect(() => {
    const confirmIntent = (async () => {
      setIsLoading(true);

      if (stripe && elements) {
        // Use the clientSecret and Elements instance to confirm the setup
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            return_url: `${window.location.origin}${returnUrl}`,
          },
          redirect: "if_required"
        });
        if (error) {
          setPaymentFailedBanner(true);
          paymentFailed(true);
        }
        else if (customer && orgSub.stripePlanSubscription) {
          // Update customer to use the new payment method as their default
          await stripe2.customers.update(
            customer.id,
            {
              invoice_settings: {
                default_payment_method: (typeof setupIntent.payment_method === "string") ? setupIntent.payment_method : undefined,
              },
            }
          );

          const currentPlanSub = await stripe2.subscriptions.retrieve(orgSub.stripePlanSubscription);

          // We'll use this array to delete all current product on the Plan Subscription (previous Subscription Products)
          const updatedItems: {}[] = currentPlanSub.items.data.map(item => ({
            id: item.id,
            deleted: true
          }));

          updatedItems.push({ price: selectedPrice.id });

          // Update Plan Subscription
          await stripe2.subscriptions.update(currentPlanSub.id, {
            items: [
              ...updatedItems,
            ],
            proration_behavior: "always_invoice",
            billing_cycle_anchor: "unchanged",
            coupon: subscriptionCoupon,
            payment_behavior: 'error_if_incomplete',
            payment_settings: { save_default_payment_method: 'off' },
            expand: ['latest_invoice.payment_intent'],
          }).then(async (data: any) => {
            const dateInSeconds = data.current_period_end;
            const renewDate = new Date(dateInSeconds * 1000);

            let nextPaymentDate = `${renewDate.toLocaleString('default', { month: 'short' })} ${renewDate.getDate()}, ${renewDate.getFullYear()}`;
            let couponDiscountAmount = '';
            let copyOfTotalDiscount = 0;

            let paymentInfo = {
              planType: PlanTypeLabels[planType],
              paymentCycle: paymentCycle !== undefined ? PaymentCycleLabel[paymentCycle] : paymentCycle,
              nextPaymentDate: nextPaymentDate,
              planCost: `$${selectedPrice && selectedPrice.unit_amount !== undefined ? (selectedPrice.unit_amount / 100).toFixed(2) : totalPrice!.toFixed(2)}`,
              totalChargedAmount: newPrice ? `$${newPrice.toFixed(2)}` : `$${totalPrice!.toFixed(2)}`
            };

            if (coupons.length > 0) {
              if (coupons[0].coupon.amount_off) {
                couponDiscountAmount = `$${(coupons[0].coupon.amount_off / 100).toFixed(2)}`;
                copyOfTotalDiscount = (coupons[0].coupon.amount_off / 100)
              } else {
                couponDiscountAmount = `$${((Number(totalPrice) * coupons[0].coupon.percent_off) / 100).toFixed(2)}`;
                copyOfTotalDiscount = ((Number(totalPrice) * coupons[0].coupon.percent_off) / 100);
              }
            }

            const copyOfTotalCharged = newPrice ? newPrice : totalPrice ? totalPrice : 0;
            const updatedTotal = copyOfTotalCharged - copyOfTotalDiscount;
            paymentInfo = { ...paymentInfo, totalChargedAmount: `$${updatedTotal.toFixed(2)}` };
            const couponInfo = coupons.length > 0 ? { couponCode: coupons[0].code, couponDiscount: coupons[0].coupon.amount_off ? `$${coupons[0].coupon.amount_off / 100}` : `${coupons[0].coupon.percent_off}%`, couponDiscountAmount: couponDiscountAmount } : null;

            try {
              FunctionService.sendPlanPurchaseConfirmationEmail(state.user!, paymentInfo, couponInfo);
            }
            catch (err) {
              console.error(err);
            }

            let org = await OrganizationService.get(state.organization.id!);

            org!.plan = { value: planType, label: PlanTypeLabels[planType] };
            org!.userLimit = UserLimitPlanType[planType];
            org!.mySymmioLicense = LicenseLimitPlanType[planType];

            await OrganizationService.save(org!)

            if (org && org.id) {
              orgSub.renewDate = renewDate.toISOString();
              orgSub.userLimit = UserLimitPlanType[planType];
              orgSub.price = (data.plan && data.plan.amount) ? (data.plan.amount / 100) : 0;
              orgSub.plan = {
                label: PlanTypeLabels[planType],
                value: planType,
              };
              orgSub.paymentCycle = {
                label: PaymentCycle[paymentCycle === PaymentCycle.Yearly ? PaymentCycle.Yearly : PaymentCycle.Monthly],
                value: paymentCycle === PaymentCycle.Yearly ? PaymentCycle.Yearly : PaymentCycle.Monthly,
              };
              orgSub.mySymmioLicense = LicenseLimitPlanType[planType];
              orgSub.accountType = {
                label: OrganizationTypeLabel[OrganizationType.Paid],
                value: OrganizationType.Paid
              };
              orgSub.trialLength = deleteField() as unknown as RadioGroupItem;
              await OrganizationSubscriptionService.save(orgSub);

            }

            paymentSuccessful(true);
          })
        }
      }
    })

    if (clientSecret) {
      confirmIntent();
    }
  }, [clientSecret])

  // Here, we're getting prorations and display coupon discounts
  useEffect(() => {
    const getFinalPrice = async () => {
      if (customer && state.organization.id && orgSub.stripePlanSubscription) {
        const currentPlanSub = await stripe2.subscriptions.retrieve(orgSub.stripePlanSubscription);

        const updatedItems: {}[] = currentPlanSub.items.data.map(item => ({
          id: item.id,
          deleted: true
        }));

        updatedItems.push({ price: selectedPrice.id });
        const invoice = await stripe2.invoices.createPreview({
          customer: customer.id,
          subscription: currentPlanSub.id,
          subscription_details: {
            items: updatedItems,
            proration_behavior: "always_invoice",
            billing_cycle_anchor: "unchanged",
          },
          coupon: subscriptionCoupon,
        });

        console.log(invoice);

        setNewPrice(invoice.amount_due / 100);

        if (invoice.lines.data && invoice.lines.data[0] !== undefined && invoice.lines.data && invoice.lines.data[0] !== null) {
          setProratedSum(invoice.lines.data[0].amount / -100);
        }
        else {
          setProratedSum(undefined);
        }

        if (invoice.discount && invoice.discount.coupon && invoice.discount.coupon.amount_off !== null) {
          setDiscountAmount(invoice.discount.coupon.amount_off / 100);
        }
        else {
          setDiscountAmount(undefined);
        }
      }
    }

    getFinalPrice();
  }, [customer, selectedPrice.id, state.organization.id, subscriptionCoupon]);

  const handleSubmit = (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    handleSubscriptionStart();
  }

  const handleSubscriptionStart = async () => {

    //e.preventDefault();
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      // handleError(submitError);
      return;
    }

    setIsLoading(true);

    // Retrieve customer from stripe by Email. Expand for Subscription
    let customerList = await stripe2.customers.list({
      limit: 1,
      email: state.user?.email,
      expand: ['data.subscriptions']
    })

    // Check if a customer was found
    if (customerList.data.length === 0 || !customerList.data[0]) {
      return;
    }

    setCustomer(customerList.data[0]);

    await stripe2.setupIntents.create({
      customer: customerList.data[0].id,
      automatic_payment_methods: {
        enabled: true,
      },
    }).then((intent: any) => {
      setClientSecret(intent.client_secret);
    });
  }


  const validateCouponCode = async (code: any) => {
    const validationErrorMessages: ValidationErrors = {
      couponIsValid: t('pricing.checkoutPage.discountErrors.couponIsNotValid'),
      couponNotExpired: t('pricing.checkoutPage.discountErrors.couponExpired'),
      couponValidForProduct: t('pricing.checkoutPage.discountErrors.couponNotValidForProduct'),
      couponValidForCustomer: t('pricing.checkoutPage.discountErrors.couponNotValidForCustomer'),
      couponMeetsMinimum: t('pricing.checkoutPage.discountErrors.couponNotMeetsMinimum', { price: (code.restrictions.minimum_amount / 100).toFixed(2) }),
      couponRedeemed: t('pricing.checkoutPage.discountErrors.couponNotRedeemed'),
    }
    let errors: any[] = [];
    const couponIsValid = code.active && code.coupon.valid;
    const couponNotExpired = (code.coupon.redeem_by ? code.coupon.redeem_by > Math.floor(Date.now() / 1000) : true) &&
      (code.expires_at ? code.expires_at > Math.floor(Date.now() / 1000) : true);
    const couponValidForProduct = code.coupon.applies_to ? !!code.coupon.applies_to.products.find((item: any) => item === selectedProduct.id) : true;
    const couponValidForCustomer = (code.customer && customer) ? customer.id === code.customer : true;
    const couponMeetsMinimum = code.restrictions.minimum_amount ? code.restrictions.minimum_amount < Number(totalPrice) : true;
    const couponRedeemed = (code.coupon.max_redemptions ? code.coupon.max_redemptions > code.coupon.times_redeemed : true) &&
      (code.max_redemptions ? code.max_redemptions > code.times_redeemed : true);
    Object.entries({ couponIsValid, couponNotExpired, couponValidForProduct, couponValidForCustomer, couponMeetsMinimum, couponRedeemed }).forEach(([key, value]) => {
      if (!value)
        errors.push(validationErrorMessages[key]);
      else if (paymentCycle === PaymentCycle.Yearly && !code.coupon.name.startsWith("Y-")) {
        errors.push(t('pricing.checkoutPage.discountErrors.cannotYearly'));
      }
      else if (paymentCycle === PaymentCycle.Monthly && !code.coupon.name.startsWith("M-")) {
        errors.push(t('pricing.checkoutPage.discountErrors.cannotMonthly'));
      }
    });

    if (errors.length > 0)
      setDiscountError(errors[0]);
    return !!(errors.length === 0);
  }

  const handleApplyCode = async () => {
    setDiscountError('');
    if (coupons.length === 0) {
      if (couponValue !== '' && couponValue !== undefined) {
        const result = allCoupons.find((item: any) => item.code === couponValue.toUpperCase());
        if (result) {
          const couponValid = await validateCouponCode(result);
          if (couponValid) {
            setCoupons([result]);
            setSubscriptionCoupon(result.coupon.id);
            setCouponValue('');
          }
        } else {
          setDiscountError(t('pricing.checkoutPage.discountErrors.enterValid'));
        }
      } else {
        setDiscountError(t('pricing.checkoutPage.discountErrors.enterValid'));
      }
    } else {
      setDiscountError(t('pricing.checkoutPage.discountErrors.only1'));
    }
  }

  useEffect(() => {
    const retrieveAllCoupons = async () => {
      setIsLoading(true);

      const coupons = await stripe2.promotionCodes.list({ expand: ['data.coupon.applies_to'] });
      setAllCoupons(coupons.data);

      if (couponId) {
        await stripe2.coupons.retrieve(couponId).then((result) => {
          if (result) {
            if (paymentCycle === PaymentCycle.Monthly && result.duration_in_months !== null && result.duration_in_months >= 1) {
              setSubscriptionCoupon(couponId);
            }
            else if (paymentCycle === PaymentCycle.Yearly && result.duration_in_months === null) {
              setSubscriptionCoupon(couponId);
            }
          }
        }).catch((e: any) => {
          console.log(e);
          setCouponExists(false);
        });

      }
      else {
        setCouponExists(false);
      }

      let customerList = await stripe2.customers.list({
        limit: 1,
        email: state.user?.email,
        expand: ['data.subscriptions']
      });

      setCustomer(customerList.data[0]);

      setIsLoading(false);
    }

    retrieveAllCoupons();
  }, [couponId, paymentCycle]);

  return (
    <div className={`${COMPONENT_CLASS}`}>
      <Loader isVisible={isLoading} />
      {paymentFailedBanner && (
        <div style={{ width: '90%', marginLeft: 'auto', marginRight: 'auto' }}>
          <WarningItem
            iconColor="#E7514F"
            onClose={() => { setPaymentFailedBanner(false); }}
            backgroundColor="#FFE9E5"
            title={t('pricing.checkoutPage.paymentDeclined.banner.title')}
            text={t('pricing.checkoutPage.paymentDeclined.banner.subText')}
          />
        </div>
      )}

      <div className={`${COMPONENT_CLASS}__container-modal`}>
        <div className={`${COMPONENT_CLASS}__summary`}>
          <div className={`${COMPONENT_CLASS}_summary_line`}>
            <div style={{ fontSize: '20px' }}>{selectedProduct.name ?? ''}</div>
            <div style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={onChangePlan}>{t('pricing.checkoutPage.changePlan')}</div>
          </div>
          {state.organization && state.organization.plan && state.organization.plan.value !== undefined && (
            <div style={{ flexDirection: 'column', alignItems: 'start' }} className={`${COMPONENT_CLASS}_summary_line`}>
              <div style={{ fontWeight: 'bold' }}>{t('pricing.checkoutPage.accChange')}</div>
              <ul style={{ marginLeft: '1rem', marginTop: '1rem', listStyleType: 'disc' }}>
                <li>{t('pricing.checkoutPage.userLimit', { starterLimit: UserLimitPlanType[state.organization.plan.value], businessLimit: UserLimitPlanType[planType] })}</li>
                <li>{t('pricing.checkoutPage.licenseLimit', { starterLimit: LicenseLimitPlanType[state.organization.plan.value], businessLimit: LicenseLimitPlanType[planType] })}</li>
                <li>
                  {paymentCycle === PaymentCycle.Yearly
                    ?
                    t('pricing.checkoutPage.year', { price: priceToShow, date: moment(orgSub?.renewDate).format('MMMM D Y') })
                    :
                    t('pricing.checkoutPage.month', { price: priceToShow, date: moment(orgSub?.renewDate).format('MMMM D Y') })}
                </li>
              </ul>
            </div>
          )}
          {proratedSum !== undefined &&
            <>
              <hr className={`${COMPONENT_CLASS}_divider ${COMPONENT_CLASS}__upgrade-divider`} />
              <div className={`${COMPONENT_CLASS}_summary_line`}>
                <div style={{ fontWeight: 'bold' }}>{t('pricing.checkoutPage.credit')}</div>
                <div>
                  ${proratedSum.toFixed(2)}
                </div>
              </div>
            </>
          }
          {discountAmount !== undefined &&
            <>
              <hr className={`${COMPONENT_CLASS}_divider ${COMPONENT_CLASS}__upgrade-divider`} />
              <div className={`${COMPONENT_CLASS}_summary_line`}>
                <div style={{ fontWeight: 'bold' }}>{t('pricing.checkoutPage.discountAmount')}</div>
                <div>
                  ${discountAmount.toFixed(2)}
                </div>
              </div>
            </>
          }
          <hr className={`${COMPONENT_CLASS}_divider ${COMPONENT_CLASS}__upgrade-divider`} />
          <div className={`${COMPONENT_CLASS}_summary_line`}>
            <div style={{ fontWeight: 'bold' }}>{t('pricing.checkoutPage.todayPay')}</div>
            {(newPrice !== undefined && newPrice !== totalPrice) &&
              <div style={{ display: 'flex' }}>
                <div style={{ textDecoration: 'line-through', marginRight: '0.5rem', color: 'gray' }}>
                  ${totalPrice?.toFixed(2) ?? '...'}
                </div>
                <div>${newPrice.toFixed(2) ?? '...'}</div>
              </div>
            }
            {(newPrice === undefined || newPrice === totalPrice) &&
              <div>
                ${totalPrice?.toFixed(2) ?? '...'}
              </div>
            }
          </div>
          {!couponExists && (
            <>
              <form style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0rem 1.25rem 1.25rem 1.25rem' }}>
                <div style={{ fontWeight: '300' }}>
                  <TextTypeInput
                    id="discountCode"
                    type="text"
                    label={t('pricing.checkoutPage.discount')}
                    inputClassName={`${`${COMPONENT_CLASS}__discount-field`} ${(discountError) && `${FORM_COMPONENT_CLASS} -showerror`}`}
                    hideLabel={true}
                    style={{ width: '100%' }}
                    registerHook={register}
                    registerOptions={{
                      value: couponValue,
                      onChange: (e) => { setCouponValue(e.target.value); },
                      onBlur: (e) => { if (discountError && e.target.value === '') { setDiscountError(''); } },
                    }} />
                </div>
                {(discountError) && (
                  <div role="alert" className={`${FORM_COMPONENT_CLASS} -coupon-error`}>
                    {discountError}
                  </div>
                )}
                <div>
                  <Button type="default" disabled={isLoading} buttonType="button" onClick={handleApplyCode} buttonText="Apply" />
                </div>
              </form><div style={{ marginLeft: '1.25rem', marginRight: '1.25rem' }}>
                {coupons.map((coupon, i) => (<TagComponent
                  id={i.toString()}
                  onCloseClick={(index) => {
                    const newCoupons = [...coupons];
                    newCoupons.splice(parseInt(index), 1);
                    setCoupons(newCoupons);
                    setSubscriptionCoupon(undefined);
                  }}
                  key={`coupon-${i}`}
                >{coupon.code}</TagComponent>))}
              </div>
            </>
          )
          }
        </div>
        <form onSubmit={handleSubmit} className={`${COMPONENT_CLASS}__form`} id="checkout-form">
          <PaymentElement />
        </form>
      </div>

      <div className={`${COMPONENT_CLASS}__bottom-text-modal`} dangerouslySetInnerHTML={{ __html: t('pricing.checkoutPage.termsAndConditions') }}>
      </div>
      <div className={`${COMPONENT_CLASS}__start-subscription`}>
        <Button
          type="link"
          inputClassName={`${COMPONENT_CLASS}__actions__cancel`}
          buttonText={t('pricing.checkoutPage.keepCurrent')}
          onClick={() => {
            setUpgradeModalOpen(false);
          }}
          disabled={isLoading}
        />
        <Button
          buttonType="submit"
          form="checkout-form"
          inputClassName="action-button"
          buttonText={t('pricing.checkoutPage.payAndUpgrade')}
          disabled={isLoading}
        />
      </div>
    </div>
  );
}

export default CheckoutFormUpdate;